import { baseRequest } from '@thrivadev/thriva-request';
import { CANCEL_SHOP_SUBSCRIPTION_API_PATH, PAUSE_SHOP_SUBSCRIPTION_API_PATH, RESUME_SHOP_SUBSCRIPTION_API_PATH, SHOP_SUBSCRIPTIONS_API_PATH, SHOP_SUBSCRIPTIONS_TYPE } from './helpers';
import { formatDataProperties } from '../utils';
/**
 * ```js
 * import { updateShopSubscription } from "@thrivadev/thriva-shop";
 * //
 * const updatedShopSubscription: ShopSubscription = await updateShopSubscription(
 *    'SUBTWUQNFV9N6OEF',
 *    {
 *      next_scheduled_date: '2023-11-01',
 *      quantity: 2,
 *      frequency_id: 'SFFXISXOC',
 *      shop_variant_id: 'SV1234'
 *    },
 *    ['frequency']
 * );
 * ```
 * Update attributes of a ShopSubscription found by using the reference id passed in the url.
 *
 * @param subscriptionId - The id of the subscription to update.
 * @param updateShopSubscriptionData - The data to update supscription.
 * @param apiOptions - The options to use for this request.
 * @returns A Promise<ShopSubscription>
 */
export function updateShopSubscription(subscriptionId, updateShopSubscriptionData, apiOptions) {
    const requestOptions = {
        method: 'PATCH',
        data: formatDataProperties(updateShopSubscriptionData, SHOP_SUBSCRIPTIONS_TYPE)
    };
    return baseRequest(`${SHOP_SUBSCRIPTIONS_API_PATH}/${subscriptionId}`, requestOptions, apiOptions);
}
/**
 * ```js
 * import { cancelShopSubscription } from "@thrivadev/thriva-shop";
 * //
 * const shopSubscription: Subscription = await cancelShopSubscription(
 *    'SUBTWUQNFV9N6OEF',
 *    {
 *      cancellation_reason: 'Too expensive'
 *     },
 *    ['frequency']
 * )
 * ```
 *
 * Cancels a shop subscription with the cancellation details passed to it and returns the cancelled subscription.
 *
 * @param subscriptionId - The id of the shop subscription to cancel.
 * @param cancelData - The cancellation reason and details.
 * @param apiOptions - The options to use for this request.
 * @returns A Promise<Subscription>
 */
export function cancelShopSubscription(subscriptionId, cancelData, apiOptions) {
    const requestOptions = {
        method: 'POST',
        data: formatDataProperties(cancelData, SHOP_SUBSCRIPTIONS_TYPE)
    };
    return baseRequest(`${SHOP_SUBSCRIPTIONS_API_PATH}/${subscriptionId}/${CANCEL_SHOP_SUBSCRIPTION_API_PATH}`, requestOptions, apiOptions);
}
/**
 * ```js
 * import { pauseShopSubscription } from "@thrivadev/thriva-shop";
 * //
 * const shopSubscription: Subscription = await pauseShopSubscription(
 *    'SUBTWUQNFV9N6OEF',
 *    {
 *      cancellation_reason: 'Too expensive'
 *     },
 *    ['frequency']
 * )
 * ```
 *
 * Pauses a shop subscription with the pausing details passed to it and returns the paused subscription.
 *
 * @param subscriptionId - The id of the shop subscription to pause.
 * @param pauseData - The cancellation reason and details.
 * @param apiOptions - The options to use for this request.
 * @returns A Promise<Subscription>
 */
export function pauseShopSubscription(subscriptionId, pauseData, apiOptions) {
    const requestOptions = {
        method: 'POST',
        data: formatDataProperties(pauseData, SHOP_SUBSCRIPTIONS_TYPE)
    };
    return baseRequest(`${SHOP_SUBSCRIPTIONS_API_PATH}/${subscriptionId}/${PAUSE_SHOP_SUBSCRIPTION_API_PATH}`, requestOptions, apiOptions);
}
/**
 * ```js
 * import { resumeShopSubscription } from "@thrivadev/thriva-shop";
 * //
 * const subscription: Subscription = await resumeShopSubscription(
 *  'SUBTWUQNFV9N6OEF',
 *  ['frequency']
 * )
 * ```
 *
 * Resume a subscription
 *
 * @param subscriptionId - The id of the subscription to resume.
 * @param apiOptions - The options to use for this request.
 * @returns A Promise<Subscription>
 */
export function resumeShopSubscription(subscriptionId, apiOptions) {
    const requestOptions = {
        method: 'POST',
        data: formatDataProperties({}, SHOP_SUBSCRIPTIONS_TYPE)
    };
    return baseRequest(`${SHOP_SUBSCRIPTIONS_API_PATH}/${subscriptionId}/${RESUME_SHOP_SUBSCRIPTION_API_PATH}`, requestOptions, apiOptions);
}
//# sourceMappingURL=update.js.map